import PictureStorageAction from '../actions/PictureStorageAction';

export interface PictureItem<Image> {
  readonly isSavedOnDisk: boolean;
  readonly isToBeSaved: boolean;
  readonly isImageDemanded: boolean;
  readonly blob: Blob | null;
  readonly image: Image | null;
}

export interface PictureStorageState<Image> {
  readonly items: { readonly [id: string]: PictureItem<Image> };
}

function finishPictureReading<Image>(
  items: { [id: string]: PictureItem<Image> },
  id: string,
  blob: Blob,
): boolean {
  if (!(id in items)) {
    return false;
  }

  let item = items[id];

  /**
   * The reading process can be invoked only when the item is saved on the disk, and workspace
   * saving process discarding the saved item must be delayed until reading is finished.
   */
  if (!item.isSavedOnDisk || item.blob !== null) {
    throw new Error('Illegal state.');
  }

  /**
   * All we do with blob is to decode the binary and save it on the disk.
   * Note that the latter is already done.
   */
  if (item.isImageDemanded || !item.isToBeSaved) {
    if (item.image === null) {
      item = { ...item, blob };
    }
  } else {
    if (item.image !== null) {
      item = { ...item, image: null };
    }
  }

  if (item === items[id]) {
    return false;
  }

  items[id] = item;
  return true;
}

function finishPictureWriting<Image>(
  items: { [id: string]: PictureItem<Image> },
  id: string,
): boolean {
  if (!(id in items)) {
    return false;
  }

  let item = items[id];

  if (!item.isSavedOnDisk) {
    item = { ...item, isSavedOnDisk: true };
  }

  if (item.isImageDemanded || !item.isToBeSaved) {
    if (item.image !== null && item.blob !== null) {
      item = { ...item, blob: null };
    }
  } else {
    if (item.image !== null || item.blob !== null) {
      item = { ...item, blob: null, image: null };
    }
  }

  if (item === items[id]) {
    return false;
  }

  items[id] = item;
  return true;
}

function finishPictureEncoding<Image>(
  items: { [id: string]: PictureItem<Image> },
  id: string,
  blob: Blob,
): boolean {
  if (!(id in items)) {
    return false;
  }

  let item = items[id];

  if (!item.isToBeSaved) {
    // Only image is required.
    return false;
  }

  if (!item.isImageDemanded && item.image !== null) {
    item = { ...item, image: null };
  }

  if (!item.isSavedOnDisk) {
    item = { ...item, blob };
  }

  if (item === items[id]) {
    return false;
  }

  items[id] = item;
  return true;
}

function finishPictureDecoding<Image>(
  items: { [id: string]: PictureItem<Image> },
  id: string,
  image: Image,
): boolean {
  if (!(id in items)) {
    return false;
  }

  let item = items[id];

  if (!item.isImageDemanded && item.isToBeSaved) {
    return false;
  }

  item = { ...item, image };

  if (item.blob !== null && (item.isSavedOnDisk || !item.isToBeSaved)) {
    item = { ...item, blob: null };
  }

  items[id] = item;
  return true;
}

function finishPictureTask<Image>(
  items: { [id: string]: PictureItem<Image> },
  taskResult: PictureStorageAction.TaskResult<Image>,
): boolean {
  switch (taskResult.type) {
    case PictureStorageAction.TaskType.Reading:
      return finishPictureReading(items, taskResult.id, taskResult.blob);

    case PictureStorageAction.TaskType.Writing:
      return finishPictureWriting(items, taskResult.id);

    case PictureStorageAction.TaskType.Encoding:
      return finishPictureEncoding(items, taskResult.id, taskResult.blob);

    case PictureStorageAction.TaskType.Decoding:
      return finishPictureDecoding(items, taskResult.id, taskResult.image);
  }
}

export function initPictureStorage<Image>(): PictureStorageState<Image> {
  return {
    items: {},
  };
}

export function initPictureStorageItems<Image>(
  state: PictureStorageState<Image>,
  isImageDemanded: (id: string) => boolean,
  ids: ReadonlyArray<string>,
): PictureStorageState<Image> {
  const items: { [id: string]: PictureItem<Image> } = {};

  for (const id of ids) {
    items[id] = {
      isSavedOnDisk: true,
      isToBeSaved: true,
      isImageDemanded: isImageDemanded(id),
      blob: null,
      image: null,
    };
  }

  if (state.items === items) {
    return state;
  }

  return { ...state, items };
}

export function createLivePictureItem<Image>(
  state: PictureStorageState<Image>,
  id: string,
  image: Image,
): PictureItem<Image> {
  let nextItem: PictureItem<Image> = {
    image,
    isSavedOnDisk: false,
    isToBeSaved: false,
    isImageDemanded: true,
    blob: null,
  };

  if (id in state.items) {
    const item = state.items[id];
    if (item.image === image && item.isImageDemanded) {
      return item;
    }

    nextItem = { ...item, image, isImageDemanded: true };
  }

  return nextItem;
}

export function preparePictureItemsForSaving<Image>(
  state: PictureStorageState<Image>,
  isToBeSaved: (id: string) => boolean,
): PictureStorageState<Image> {
  let modified = false;
  const items = { ...state.items };

  for (const id in state.items) {
    const item = state.items[id];
    const nextIsToBeSaved = isToBeSaved(id);

    if (item.isToBeSaved !== nextIsToBeSaved) {
      modified = true;
      items[id] = {
        ...item,
        isToBeSaved: nextIsToBeSaved,
      };
    }
  }

  return modified ? { ...state, items } : state;
}

export function finalizePictureItemsForSaving<Image>(
  state: PictureStorageState<Image>,
): PictureStorageState<Image> {
  let modified = false;
  const items = { ...state.items };

  for (const id in state.items) {
    const item = state.items[id];

    /**
     * Assumes that the change listener removes unnecessary items on the disk actually.
     * Process for items which are saved on the disk is done by finishPictureWriting.
     */
    if (!item.isToBeSaved && item.isSavedOnDisk) {
      modified = true;
      items[id] = {
        ...item,
        isSavedOnDisk: false,
      };
    }
  }

  return modified ? { ...state, items } : state;
}

export function preparePictureItemsForDrawing<Image>(
  state: PictureStorageState<Image>,
  isImageDemanded: (id: string) => boolean,
): PictureStorageState<Image> {
  let modified = false;
  const items = { ...state.items };

  for (const id in state.items) {
    const item = state.items[id];

    const nextIsImageDemanded = isImageDemanded(id);
    if (item.isImageDemanded === nextIsImageDemanded) {
      continue;
    }

    let nextItem: PictureItem<Image> = { ...item, isImageDemanded: nextIsImageDemanded };

    if (
      !nextIsImageDemanded
      && item.isSavedOnDisk
      && (item.image !== null || item.blob !== null)
    ) {
      nextItem = { ...nextItem, image: null, blob: null };
    }

    modified = true;
    items[id] = nextItem;
  }

  return modified ? { ...state, items } : state;
}

export function prunePictureItems<Image>(
  state: PictureStorageState<Image>,
  isUsed: (id: string) => boolean,
): PictureStorageState<Image> {
  const nextItems = { ...state.items };
  for (const id in state.items) {
    const item = state.items[id];

    if (item.isToBeSaved && !item.isSavedOnDisk) {
      continue;
    }

    if (!isUsed(id)) {
      delete nextItems[id];
    }
  }

  return Object.keys(state.items).length === Object.keys(nextItems).length ? state : {
    ...state,
    items: nextItems,
  };
}

export function isPictureSavingFinished<Image>(state: PictureStorageState<Image>) {
  for (const id in state.items) {
    const item = state.items[id];

    if (item.isToBeSaved) {
      if (!item.isSavedOnDisk) {
        return false;
      }
    } else {
      // The item is going to be removed from the disk. Backup must be done.
      if (item.blob === null && item.image === null) {
        return false;
      }
    }
  }

  return true;
}

export default function pictureStorage<Image>(
  state: PictureStorageState<Image>,
  action: PictureStorageAction.Action<Image>,
): PictureStorageState<Image> {
  switch (action.type) {
    case PictureStorageAction.ActionType.FINISH_TASKS: {
      let modified = false;
      const items = { ...state.items };

      // Avoid reducer style. Copying state object takes a huge performance penalty.
      for (const taskResult of action.taskResults) {
        modified = finishPictureTask(items, taskResult) || modified;
      }

      return modified ? { ...state, items } : state;
    }
  }
}
